import Box from "@mui/material/Box";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import AdministrationPermissionsLoadState from "legal-entities/administrators/_views/administration-permissions-load-state";
import {
  type MRT_RowData as RowData,
  type MRT_TableInstance as TableInstance,
} from "material-react-table";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import PermissionsPageTitle from "permissions/_views/tables/permissions-page-title";
import { getPermissionsViewMode } from "permissions/_views/tables/_utilities/permissions-route-state";
import PermissionsTable from "permissions/_views/tables/permissions-table";
import { JSX, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { useSession } from "users/__session/session-context";

export default function PermissionsPage() {
  const location = useLocation();
  const dispatch = useAttorneyHubDispatch();
  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);
  const viewMode = getPermissionsViewMode(location.search);
  const showRouteTitle = viewMode !== "home" && viewMode !== "grantee";
  const showFocusedMatrixTitleAboveTable =
    viewMode === "matrix" && Boolean(new URLSearchParams(location.search).get("granteeUserId"));

  useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  const retrySessionPermissions = useCallback(() => {
    if (!sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissionsLoading]);

  const renderTopToolbarCustomActions = <T extends RowData>(
    _props: { table: TableInstance<T> },
    additional?: JSX.Element,
    filters?: JSX.Element,
  ) => (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      {showRouteTitle && !showFocusedMatrixTitleAboveTable && <PermissionsPageTitle />}
      {additional && <Box>{additional}</Box>}
      {filters}
    </Box>
  );

  if (!sessionPermissions) {
    return <AdministrationPermissionsLoadState error={sessionPermissionsError} onRetry={retrySessionPermissions} />;
  }
  if (!sessionPermissions.includesAccessToAdministration) return <NotAuthorized />;

  if (showFocusedMatrixTitleAboveTable) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
        <Box sx={{ px: 1, pt: 1 }}>
          <PermissionsPageTitle />
        </Box>
        <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
          <PermissionsTable renderTopToolbarCustomActions={renderTopToolbarCustomActions} />
        </Box>
      </Box>
    );
  }

  if (viewMode === "grantee" || viewMode === "create-template") {
    return (
      <Box sx={{ display: "flex", height: "100%", minHeight: 0 }}>
        <PermissionsTable renderTopToolbarCustomActions={renderTopToolbarCustomActions} />
      </Box>
    );
  }

  return <PermissionsTable renderTopToolbarCustomActions={renderTopToolbarCustomActions} />;
}